import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import type { SxProps, Theme } from "@mui/material/styles";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import { useTranslation } from "react-i18next";
import type { CargoOnboardGridItem } from "../cargoOnboard";

type CargoOnboardGridProps = {
  items: CargoOnboardGridItem[];
  onEdit?: (item: CargoOnboardGridItem) => void;
  onDelete?: (item: CargoOnboardGridItem) => void;
  customSx?: SxProps<Theme>;
  testId?: string;
};

const PAGE_SIZE = 10;

const CargoOnboardGrid = ({
  items,
  onEdit,
  onDelete,
  customSx,
  testId = "cargo-onboard-grid",
}: CargoOnboardGridProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const headers = useMemo<Header[]>(() => {
    const base: Header[] = [
      {
        field: "cargoType",
        columnName: t("vessel-call.create.cargo-onboard.grid-headers.cargo-type"),
        flex: 1.4,
        minWidth: 180,
      },
      {
        field: "quantity",
        columnName: t("vessel-call.create.cargo-onboard.grid-headers.quantity"),
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: "unitOfMeasurement",
        columnName: t("vessel-call.create.cargo-onboard.grid-headers.unit-of-measurement"),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "hazardousAndDangerousCargo",
        columnName: t(
          "vessel-call.create.cargo-onboard.grid-headers.hazardous-and-dangerous-cargo",
        ),
        flex: 1,
        minWidth: 170,
      },
      {
        field: "imoHazardClasses",
        columnName: t("vessel-call.create.cargo-onboard.grid-headers.imo-hazard-classes"),
        flex: 1,
        minWidth: 160,
      },
    ];

    if (onEdit || onDelete) {
      base.push({
        field: "action",
        columnName: t("vessel-call.create.cargo-onboard.grid-headers.actions"),
        width: 120,
        minWidth: 120,
      });
    }

    return base;
  }, [onDelete, onEdit, t]);

  const rows = useMemo(
    () =>
      items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((item) => ({
        id: item.id,
        cargoType: item.cargoType,
        quantity: item.quantity ?? "-",
        unitOfMeasurement: item.unitOfMeasurement || "-",
        hazardousAndDangerousCargo: item.hazardousAndDangerousCargo
          ? t("common.yes")
          : t("common.no"),
        imoHazardClasses: item.imoHazardClasses || "-",
      })),
    [items, page, t],
  );

  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      <BaseTable
        rows={rows}
        headers={headers}
        onRowEdit={
          onEdit ? (row) => onEdit(items.find((item) => item.id === row.id) ?? items[0]) : undefined
        }
        onRowDelete={
          onDelete
            ? (row) => onDelete(items.find((item) => item.id === row.id) ?? items[0])
            : undefined
        }
        customSx={customSx}
      />
      {pageCount > 1 && (
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} />
        </Box>
      )}
    </div>
  );
};

export default CargoOnboardGrid;
